import { TInvitedHA } from "models/healthAuthorities.models";
import {
  IProjectConfiguration,
  ProjectConfiguration,
} from "models/projectConfigurations.models";
import { IProject } from "models/projects.models";
import {
  InvitationStatus,
  PENDING_INVITATION_STATUSES,
} from "utils/constants/healthAuthorities.constants";
import {
  ProjectTemplateType,
  ProjectType,
} from "utils/constants/project.constants";

export const checkIsRelianceProject = (
  project: IProject | null | undefined,
) => {
  return project?.type === ProjectType.Reliance;
};

export const checkIsRegulatoryReviewProject = (
  project: IProject | null | undefined,
) => {
  return project?.type === ProjectType.RegulatoryReview;
};

export const checkHasRelianceTemplate = (
  project: Pick<IProject, "type" | "templateType"> | null | undefined,
) =>
  project?.type === ProjectType.Reliance ||
  (project?.type === ProjectType.SharedWorkspace &&
    project?.templateType === ProjectTemplateType.Reliance);

export const checkHasRegulatoryReviewTemplate = (
  project: Pick<IProject, "type" | "templateType"> | null | undefined,
) =>
  project?.type === ProjectType.RegulatoryReview ||
  (project?.type === ProjectType.SharedWorkspace &&
    project?.templateType === ProjectTemplateType.RegulatoryReview);

export const getConfigurationValue = (
  configurationName: ProjectConfiguration,
  configurationObject: IProjectConfiguration | undefined,
) => configurationObject?.[configurationName];

export const getProjectTypeFromTemplateType = (
  projectTemplateType: ProjectTemplateType,
): ProjectType | undefined => {
  switch (projectTemplateType) {
    case ProjectTemplateType.Reliance:
      return ProjectType.Reliance;

    case ProjectTemplateType.RegulatoryReview:
      return ProjectType.RegulatoryReview;

    default:
      return undefined;
  }
};

type TProjectWithInvitedParticipants = {
  metadata?: {
    invitedParticipants?: TInvitedHA[];
  };
};

export const getInvitationCounts = (
  project: TProjectWithInvitedParticipants | null | undefined,
) => {
  const invited = project?.metadata?.invitedParticipants ?? [];
  const invitedTenantIds = new Set(invited.map(({ tenantId }) => tenantId));

  const getTenantIdsByStatus = (
    predicate: (responseStatus: InvitationStatus | null) => boolean,
  ) =>
    new Set(
      invited
        .filter(({ responseStatus }) => predicate(responseStatus))
        .map(({ tenantId }) => tenantId),
    );

  const acceptedTenantIds = getTenantIdsByStatus(
    (responseStatus) => responseStatus === InvitationStatus.Accepted,
  );

  const pendingAcceptanceTenantIds = getTenantIdsByStatus(
    (responseStatus) =>
      !!responseStatus && PENDING_INVITATION_STATUSES.includes(responseStatus),
  );

  const declinedTenantIds = getTenantIdsByStatus(
    (responseStatus) => responseStatus === InvitationStatus.Declined,
  );

  return {
    totalInvited: invitedTenantIds.size,
    accepted: acceptedTenantIds.size,
    pendingAcceptance: pendingAcceptanceTenantIds.size,
    declined: declinedTenantIds.size,
  };
};
